import { useEffect, useState } from 'react';
import { alertaGeneral } from '../../utils/alertas';
import apiClient from '../../utils/axiosPetition';

const normalizeSplitReceivers = (receivers) => {
  if (Array.isArray(receivers)) return receivers;
  if (typeof receivers === 'string') {
    try {
      const parsed = JSON.parse(receivers);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const BotonSubmitEpayco = ({ doctorId = '', descripcion, correo, disabled = false, redireccion, disabledFunction, amount, invoice, extraData, registrationData, prominent = false }) => {
  const [loading, setLoading] = useState(false);

  const valorFormateado = amount != null && amount !== '' ? `$${Number(amount).toLocaleString('es-CO')}` : null;

  const buildPatientFullName = (data) =>
    [data.firstName, data.secondName, data.firstLastName, data.secondLastName]
      .filter(Boolean)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

  const isPatientPremiumRegistration =
    extraData?.tipo === 'paciente_premium' ||
    Boolean(registrationData?.firstName && registrationData?.firstLastName && !registrationData?.service);

  useEffect(() => {
    // Cargar el script de ePayco solo una vez
    if (!document.getElementById('epayco-checkout-js')) {
      const script = document.createElement('script');
      script.id = 'epayco-checkout-js';
      script.src = 'https://checkout.epayco.co/checkout.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const handlePago = async (e) => {
    e.preventDefault();
    
    // Primero validar campos únicos si hay registrationData (flujo de registro de afiliado)
    if (registrationData && Object.keys(registrationData).length > 0) {
      if (disabledFunction) {
        const isDisabled = await disabledFunction();
        if (isDisabled) {
          console.log('❌ Validación de campos únicos falló');
          return;
        }
      }
    } else if (disabledFunction) {
      // Si no hay registrationData pero hay disabledFunction, ejecutarlo de todas formas
      const isDisabled = await disabledFunction();
      if (isDisabled) return;
    }
    
    // Guardar datos de registro en localStorage antes de abrir el checkout
    // Esto es necesario para el flujo de registro de afiliados
    // Verificar que registrationData tenga los datos necesarios
    if (registrationData && Object.keys(registrationData).length > 0) {
      if (isPatientPremiumRegistration) {
        const fullName = buildPatientFullName(registrationData);
        if (!registrationData.email || !registrationData.firstName || !registrationData.firstLastName || !registrationData.cellNumber) {
          alertaGeneral({
            titulo: 'Error',
            messageHtml: 'Completa nombre, apellido, correo y celular antes de continuar.',
            isError: true
          });
          return;
        }
        if (registrationData.password && registrationData.password.length < 8) {
          alertaGeneral({
            titulo: 'Error',
            messageHtml: 'Debes ingresar una contraseña de al menos 8 caracteres (con mayúscula, número y símbolo).',
            isError: true
          });
          return;
        }

        const normalizedData = {
          ...registrationData,
          email: registrationData.email.toLowerCase().trim(),
          cellNumber: registrationData.cellNumber || '',
          fullName,
          registrationType: 'paciente_premium',
        };

        localStorage.setItem('registro-data', JSON.stringify(normalizedData));
        sessionStorage.setItem('registro-data-backup', JSON.stringify(normalizedData));
      } else {
      // Validar que tenga los campos mínimos necesarios
      if (!registrationData.email || !registrationData.fullName) {
        console.error('❌ Error: Faltan datos esenciales en registrationData:', registrationData);
        alertaGeneral({
          titulo: 'Error',
          messageHtml: 'Por favor completa todos los campos requeridos (nombre completo y correo electrónico).',
          isError: true
        });
        return;
      }
      
      // Validar campos adicionales para registro de afiliado (incluye contraseña elegida por el doctor)
      if (!registrationData.cellNumber || !registrationData.idNumber || !registrationData.service) {
        console.error('❌ Error: Faltan datos adicionales en registrationData');
        alertaGeneral({
          titulo: 'Error',
          messageHtml: 'Por favor completa todos los campos del formulario (celular, cédula y servicio) antes de continuar.',
          isError: true
        });
        return;
      }
      if (!registrationData.password || registrationData.password.length < 8) {
        alertaGeneral({
          titulo: 'Error',
          messageHtml: 'Debes ingresar una contraseña de al menos 8 caracteres (con mayúscula, número y símbolo).',
          isError: true
        });
        return;
      }
      
      // Normalizar el correo (minúsculas y sin espacios) para consistencia
      const normalizedData = {
        ...registrationData,
        email: registrationData.email ? registrationData.email.toLowerCase().trim() : registrationData.email,
        cellNumber: registrationData.cellNumber || '',
        idNumber: registrationData.idNumber || '',
        service: registrationData.service || '',
        fullName: registrationData.fullName || ''
      };
      
      console.log('💾 Preparando datos para guardar (email, fullName, cellNumber, idNumber, service)');
      
      // Guardar en localStorage (incluye password para completar registro tras el pago)
      localStorage.setItem('registro-data', JSON.stringify(normalizedData));
      console.log('✅ Datos de registro guardados en localStorage');
      
      // También guardar en sessionStorage como respaldo
      sessionStorage.setItem('registro-data-backup', JSON.stringify(normalizedData));
      console.log('✅ Backup guardado en sessionStorage');
      }
    }
    
    setLoading(true);
    // Llama al backend para obtener los datos de la transacción ePayco
    apiClient.post('/api/epayco/procesar-pago', {
      doctorId,
      descripcion,
      correo,
      amount,
      invoice,
      redireccion,
      ...extraData
    })
      .then(res => {
        // Configura y abre el checkout de ePayco
        if (window.ePayco) {
          const handler = window.ePayco.checkout.configure({
            key: res.data.publicKey,
            test: res.data.test
          });
          const data = {
            name: res.data.description,
            description: res.data.description,
            invoice: res.data.referenceCode,
            currency: 'cop',
            amount: String(res.data.precio),
            tax_base: '0',
            tax: '0',
            country: 'CO',
            lang: 'es',
            email_billing: res.data.correo,
            response: res.data.responseUrl,
            confirmation: res.data.confirmationUrl,
            split_app_id: res.data.split_app_id,
            split_merchant_id: res.data.split_merchant_id,
            split_type: res.data.split_type,
            split_primary_receiver: res.data.split_primary_receiver,
            split_primary_receiver_fee: res.data.split_primary_receiver_fee,
            splitpayment: res.data.splitpayment,
            split_receivers: normalizeSplitReceivers(res.data.split_receivers),
            // Campos personalizados para backend
            x_extra1: res.data.x_extra1 || extraData?.x_extra1 || '',
            x_extra2: res.data.x_extra2 || extraData?.x_extra2 || '',
            x_extra3: res.data.x_extra3 || extraData?.x_extra3 || '',
            x_extra4: res.data.x_extra4 || extraData?.x_extra4 || '',
            x_extra5: res.data.x_extra5 || extraData?.x_extra5 || '',
            x_extra6: res.data.x_extra6 || extraData?.x_extra6 || '',
            x_extra7: res.data.x_extra7 || extraData?.x_extra7 || '',
            x_extra8: res.data.x_extra8 || extraData?.x_extra8 || '',
            x_extra9: res.data.x_extra9 || extraData?.x_extra9 || '',
            x_extra10: res.data.x_extra10 || extraData?.x_extra10 || '',
            // ...otros campos opcionales
          };
          handler.open(data);
        } else {
          alertaGeneral({
            titulo: 'Error',
            messageHtml: 'No se pudo cargar el checkout de ePayco',
            isError: true
          });
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.message || 'Ocurrió un error al procesar el pago con ePayco';
        alertaGeneral({
          titulo: 'Error',
          messageHtml: msg,
          isError: true
        });
      })
      .finally(() => setLoading(false));
  };

  const baseClasses = 'w-full font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center gap-2';
  const prominentClasses = prominent || valorFormateado
    ? `px-5 py-3.5 text-base rounded-xl shadow-md ${disabled ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-[#f79865] hover:bg-[#e88a55] text-white border-2 border-[#e88a55] hover:shadow-lg focus:ring-[#f79865]'}`
    : `px-4 py-2.5 text-sm rounded-xl ${disabled ? 'bg-gray-400 cursor-not-allowed text-white' : 'bg-[#f79865] hover:bg-[#e88a55] text-white border-2 border-[#e88a55] hover:shadow-md focus:ring-[#f79865]'}`;

  return (
    <button
      className={`${baseClasses} ${prominentClasses}`}
      type="button"
      onClick={handlePago}
      disabled={disabled || loading}
    >
      {loading ? (
        'Procesando...'
      ) : valorFormateado ? (
        <>
          <span>Pagar</span>
          <span className="text-lg font-bold tracking-tight drop-shadow-sm">{valorFormateado}</span>
          <span>con ePayco</span>
        </>
      ) : (
        'Pagar con ePayco'
      )}
    </button>
  );
};

export default BotonSubmitEpayco; 